import { Box, CircularProgress } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useGetCamerasListQuery } from '@api/connectOperations';
import { useInterval } from '@hooks/useInterval';
import isUndefined from 'lodash/fp/isUndefined';
import { toast } from 'sonner';
import { rdBackSignalr, rdCameraSignalr } from 'src/api/signalr';
import { useRDInstance } from 'src/hooks/useRdInstance';
import { useSetDefaultCameraMutation } from 'src/redux/api/connectInstance';
import { useAuth } from 'src/routes/Auth/authContext';

import ChooseCameraButton from './ChooseCameraButton';
import sx from './styles';

const DeviceCamera: FC = () => {
  const [image, setImage] = useState<string>('');
  const { myConnectedInstance } = useRDInstance();
  const userDeviceId = myConnectedInstance?.id;
  const [selectedCameraIndex, setSelectedCameraIndex] = useState<number | null>(null);
  const { data } = useGetCamerasListQuery({ rdConnectInstanceId: userDeviceId });
  const [saveCamera, { isLoading }] = useSetDefaultCameraMutation();
  const cameraRef = useRef(selectedCameraIndex);

  cameraRef.current = selectedCameraIndex;

  const defaultCameraIndex = data?.cameras?.find((camera) => camera.isDefault)?.index;

  const cameraIndex = selectedCameraIndex ?? defaultCameraIndex;

  const { user } = useAuth();

  // remove when /api/RdConnectOperations/start-capturing/{receiveSessionId}/{rdConnectInstanceId}/{index} enpoint remive index option
  useEffect(() => {
    return () => {
      if (cameraRef.current !== null && cameraRef.current !== defaultCameraIndex && !isLoading) {
        saveCamera({ id: userDeviceId || 0, cameraIndex: cameraRef.current, isDefault: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUndefined(userDeviceId) || !user?.access_token) {
      return;
    }
    if (!rdBackSignalr.connection) {
      toast.error('Stream error');
    }
    rdCameraSignalr.connect(user?.access_token);
    rdCameraSignalr.registerEvent('OnCameraFrame', 'deviceCamera', (payload) => {
      const url = URL.createObjectURL(new Blob([payload.JpegBytes], { type: 'image/jpeg' }));
      setImage(url);
    });
    rdBackSignalr.connection?.send('PushCameraAlive', userDeviceId, {
      Index: cameraIndex,
      TimestampUtc: new Date().toISOString(),
    });
    return () => {
      rdCameraSignalr.disconnect();
      rdCameraSignalr.unregisterEvent('OnCameraFrame', 'deviceCamera');
    };
  }, [user?.access_token, userDeviceId, cameraIndex]);

  const fetchCameraImage = useCallback(async () => {
    if (isUndefined(userDeviceId) || isUndefined(cameraIndex)) {
      return;
    }

    try {
      rdBackSignalr.connection?.send('PushCameraAlive', userDeviceId, {
        Index: cameraIndex,
        TimestampUtc: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error fetching camera image:', error);
    }
  }, [cameraIndex, userDeviceId]);

  useInterval(fetchCameraImage, 4000);

  return (
    <Box sx={sx.container}>
      <ChooseCameraButton
        cameraIndex={cameraIndex}
        cameras={data?.cameras}
        setUserCameraIndex={setSelectedCameraIndex}
      />

      {image ? (
        <Box component="img" src={image} sx={sx.img} alt="Camera preview" />
      ) : (
        <CircularProgress aria-label="Loading..." sx={sx.loader} />
      )}
    </Box>
  );
};

export default DeviceCamera;
